import { useMemo, useState } from 'react';
import type { ProposalInfo } from '@/types/governance';
import {
  getProposalStatusLabel,
  getProposalStatusColor,
  calculateVotePercentages,
  ProposalStatus,
} from '@/types/governance';
import { formatDistanceToNow, format } from 'date-fns';

interface ProposalListProps {
  proposals: ProposalInfo[];
  onVote?: (proposal: ProposalInfo) => void;
  isLoading?: boolean;
}

type SortOption = 'newest' | 'oldest' | 'status';
type FilterOption = 'all' | 'active' | 'passed' | 'rejected';

export function ProposalList({
  proposals,
  onVote,
  isLoading = false,
}: ProposalListProps): JSX.Element {
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');

  const filteredAndSortedProposals = useMemo(() => {
    let result = [...proposals];

    // Apply filter
    switch (filterBy) {
      case 'active':
        result = result.filter((p) => (p.status as ProposalStatus) === ProposalStatus.VOTING_PERIOD);
        break;
      case 'passed':
        result = result.filter((p) => (p.status as ProposalStatus) === ProposalStatus.PASSED);
        break;
      case 'rejected':
        result = result.filter(
          (p) => (p.status as ProposalStatus) === ProposalStatus.REJECTED || (p.status as ProposalStatus) === ProposalStatus.FAILED
        );
        break;
    }

    // Apply sort
    switch (sortBy) {
      case 'newest':
        result.sort((a, b) => b.proposal_id - a.proposal_id);
        break;
      case 'oldest':
        result.sort((a, b) => a.proposal_id - b.proposal_id);
        break;
      case 'status':
        result.sort((a, b) => a.status - b.status);
        break;
    }

    return result;
  }, [proposals, sortBy, filterBy]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (proposals.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        No proposals found
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters and Sort */}
      <div className="flex flex-wrap gap-4 items-center justify-between bg-white p-4 rounded-lg shadow-sm">
        <div className="flex gap-2">
          {(['all', 'active', 'passed', 'rejected'] as const).map((filter) => (
            <button
              key={filter}
              onClick={() => setFilterBy(filter)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                filterBy === filter
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="sort" className="text-sm text-gray-600 font-medium">
            Sort by:
          </label>
          <select
            id="sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white text-gray-900"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="status">Status</option>
          </select>
        </div>
      </div>

      {/* Proposal Cards */}
      <div className="space-y-4">
        {filteredAndSortedProposals.map((proposal) => (
          <ProposalCard key={proposal.proposal_id} proposal={proposal} onVote={onVote} />
        ))}
      </div>

      {filteredAndSortedProposals.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No proposals match the current filter
        </div>
      )}
    </div>
  );
}

interface ProposalCardProps {
  proposal: ProposalInfo;
  onVote?: (proposal: ProposalInfo) => void;
}

function ProposalCard({ proposal, onVote }: ProposalCardProps): JSX.Element {
  const percentages = calculateVotePercentages(proposal.final_tally_result);
  const isActive = (proposal.status as ProposalStatus) === ProposalStatus.VOTING_PERIOD;
  const title = proposal.content?.title ?? `Proposal #${proposal.proposal_id}`;
  const description = proposal.content?.description ?? '';

  const formatDate = (dateStr: string | null): string => {
    if (dateStr === null || dateStr === '') return 'N/A';
    try {
      const date = new Date(dateStr);
      return format(date, 'MMM d, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const getTimeRemaining = (endTime: string | null): string => {
    if (endTime === null || endTime === '') return '';
    try {
      const end = new Date(endTime);
      if (end < new Date()) return 'Ended';
      return `Ends ${formatDistanceToNow(end, { addSuffix: true })}`;
    } catch {
      return '';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-sm text-gray-500 font-medium">
              #{proposal.proposal_id}
            </span>
            <span
              className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getProposalStatusColor(
                proposal.status
              )}`}
            >
              {getProposalStatusLabel(proposal.status)}
            </span>
            {isActive && proposal.voting_end_time !== null && proposal.voting_end_time !== '' && (
              <span className="text-xs text-blue-600 font-medium">
                {getTimeRemaining(proposal.voting_end_time)}
              </span>
            )}
          </div>
          <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
            {title}
          </h3>
        </div>
        {isActive && onVote && (
          <button
            onClick={() => onVote(proposal)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex-shrink-0"
          >
            Vote
          </button>
        )}
      </div>

      {/* Description */}
      {description !== null && description !== '' && (
        <p className="text-gray-600 text-sm mb-4 line-clamp-3">{description}</p>
      )}

      {/* Vote Progress Bar */}
      <div className="mb-4">
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex">
          <div
            className="bg-green-500 transition-all"
            style={{ width: `${percentages.yes}%` }}
            title={`Yes: ${percentages.yes.toFixed(1)}%`}
          />
          <div
            className="bg-gray-400 transition-all"
            style={{ width: `${percentages.abstain}%` }}
            title={`Abstain: ${percentages.abstain.toFixed(1)}%`}
          />
          <div
            className="bg-red-500 transition-all"
            style={{ width: `${percentages.no}%` }}
            title={`No: ${percentages.no.toFixed(1)}%`}
          />
          <div
            className="bg-red-800 transition-all"
            style={{ width: `${percentages.noWithVeto}%` }}
            title={`No with Veto: ${percentages.noWithVeto.toFixed(1)}%`}
          />
        </div>
        <div className="flex justify-between text-xs mt-1 text-gray-600">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            Yes {percentages.yes.toFixed(1)}%
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
            Abstain {percentages.abstain.toFixed(1)}%
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
            No {percentages.no.toFixed(1)}%
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-red-800 rounded-full"></span>
            Veto {percentages.noWithVeto.toFixed(1)}%
          </span>
        </div>
      </div>

      {/* Dates */}
      <div className="flex gap-6 text-xs text-gray-500">
        <div>
          <span className="font-medium">Submitted:</span> {formatDate(proposal.submit_time)}
        </div>
        {(proposal.status as ProposalStatus) === ProposalStatus.DEPOSIT_PERIOD && (
          <div>
            <span className="font-medium">Deposit End:</span>{' '}
            {formatDate(proposal.deposit_end_time)}
          </div>
        )}
        {((proposal.status as ProposalStatus) === ProposalStatus.VOTING_PERIOD ||
          (proposal.status as ProposalStatus) === ProposalStatus.PASSED ||
          (proposal.status as ProposalStatus) === ProposalStatus.REJECTED) && (
          <div>
            <span className="font-medium">Voting End:</span>{' '}
            {formatDate(proposal.voting_end_time)}
          </div>
        )}
      </div>
    </div>
  );
}
